import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { InputLabel } from "src/components";
import { colors, colors as themeColors } from "src/theme";

const Container = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const TextContainer = styled.div({
  flex: 1,
});

const NumberInputContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  border: `1px solid ${themeColors.border5}`,
  borderRadius: '8px',
  overflow: 'hidden',
  backgroundColor: themeColors.background7,
});

const Button = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '44px',
  height: '44px',
  border: 'none',
  backgroundColor: 'transparent',
  color: themeColors.text2,
  fontSize: '18px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease-in-out',

  '&:hover': {
    backgroundColor: themeColors.background6,
  },

  '&:active': {
    backgroundColor: themeColors.background5,
  },

  '&:disabled': {
    color: themeColors.text4,
    cursor: 'not-allowed',
    backgroundColor: 'transparent',
  },
});

const NumberDisplay = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '44px',
  padding: '0 12px',
  fontSize: '18px',
  fontWeight: '400',
  color: themeColors.text1,
  backgroundColor: 'transparent',
  border: 'none',
  textAlign: 'center',
  userSelect: 'none',
});

const InputLabelStyle = styled(InputLabel)({
  marginBottom: '4px',
});

const DescriptionText = styled.div({
  fontSize: '12px',
  color: themeColors.text4,
  lineHeight: '16px'
});


export const ValidationErrorStyleNew = styled.div({
  position: 'absolute',
  color: colors.error1,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '1.14',
  marginBottom: 0,
  paddingTop: '75px',
  transition: 'all 0.4s ease',
});

export type InputNumWithIconsProps = {
  label?: string;
  description?: string;
  value?: number;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  error?: string;
  className?: string;
};

export const InputNumWithIcons: React.FC<InputNumWithIconsProps> = ({
  label,
  description,
  value = 0,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  disabled = false,
  error = '',
  className,
}) => {
  const [currentValue, setCurrentValue] = useState<number>(value);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const handleIncrement = () => {
    if (disabled) return;
    const newValue = Math.min(currentValue + step, max);
    setCurrentValue(newValue);
    if (onChange) onChange(newValue);
  };

  const handleDecrement = () => {
    if (disabled) return;
    const newValue = Math.max(currentValue - step, min);
    setCurrentValue(newValue);
    if (onChange) onChange(newValue);
  };

  const isDecrementDisabled = disabled || currentValue <= min;
  const isIncrementDisabled = disabled || currentValue >= max;

  return (
    <Container className={className}>
      <TextContainer>
        {label && <InputLabelStyle disabled={disabled}>{label}</InputLabelStyle>}
        {description && <DescriptionText>{description}</DescriptionText>}
      </TextContainer>
      
      <NumberInputContainer>
        <Button
          onClick={handleDecrement}
          disabled={isDecrementDisabled}
          type="button"
        >
          -
        </Button>
        
        <NumberDisplay>
          {currentValue}
        </NumberDisplay>
        
        <Button
          onClick={handleIncrement}
          disabled={isIncrementDisabled}
          type="button"
        >
          +
        </Button>
      </NumberInputContainer>
      
      {error && <ValidationErrorStyleNew>{error}</ValidationErrorStyleNew>}
    </Container>
  );
};
